import { useCallback, useRef } from 'react';
import { Box } from '@/UI/Box/Box';
import { color } from '@/colors';
import { useVideoState, useVideoView } from '@/components/Recipe/FlowContext';
import { themeColor } from '@/UI/themeColors';
import { FileViewer } from '@/components/Nodes/Shared/FileViewer';
import type { DragEvent, MouseEvent, PointerEvent } from 'react';
import type { UploadedAsset } from '@/types/api/assets';
import type { FileKind, NodeId } from 'web';

export interface GridItemProps {
  nodeId: NodeId;
  file: FileKind | UploadedAsset;
  index: number;
  isSelected: boolean;
  onFileClick: (index: number) => void;
  onContextMenu?: (event: MouseEvent, index: number) => void;
  onTextChange?: (index: number, newValue: string) => void;
  fixedItemSize?: number;
  fillHeight?: boolean;
  /** True when feature + multi-file grid eligibility are satisfied (see GridViewer). */
  mediaToCanvasDragActive?: boolean;
  onMediaToCanvasDragStart?: (event: DragEvent, index: number) => void;
  /** Move (not duplicate): source tile stays visible at reduced opacity until drag ends. */
  mediaToCanvasDragSourceDimmed?: boolean;
  onMediaToCanvasDragModifier?: (duplicateModifier: boolean) => void;
  onMediaToCanvasDragEnd?: () => void;
  /** While dragging to canvas, keep hover previews from playing audio in the background. */
  suspendMediaPlayback?: boolean;
}

export const GridItem = ({
  nodeId,
  file,
  index,
  isSelected,
  onFileClick,
  onContextMenu,
  onTextChange,
  fixedItemSize,
  fillHeight = false,
  mediaToCanvasDragActive = false,
  onMediaToCanvasDragStart,
  mediaToCanvasDragSourceDimmed = false,
  onMediaToCanvasDragModifier,
  onMediaToCanvasDragEnd,
  suspendMediaPlayback = false,
}: GridItemProps) => {
  const videoState = useVideoState(nodeId, index);
  const videoView = useVideoView(nodeId, index);
  const suppressClickUntilRef = useRef(0);
  const dragInFlightRef = useRef(false);
  const handleFileClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (Date.now() < suppressClickUntilRef.current) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      onFileClick(index);
    },
    [onFileClick, index],
  );

  const handleContextMenuForItem = useCallback(
    (event: MouseEvent<HTMLDivElement>) => onContextMenu?.(event, index),
    [onContextMenu, index],
  );

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (mediaToCanvasDragActive) {
        event.stopPropagation();
      }
    },
    [mediaToCanvasDragActive],
  );

  const handleDragStart = useCallback(
    (event: DragEvent) => {
      if (!mediaToCanvasDragActive || !onMediaToCanvasDragStart) return;
      if (file.type === 'video') {
        videoView.pauseIfPlaying();
      }
      onMediaToCanvasDragStart(event, index);
      if (event.defaultPrevented) return;
      dragInFlightRef.current = true;
      suppressClickUntilRef.current = Date.now() + 500;
    },
    [mediaToCanvasDragActive, onMediaToCanvasDragStart, index, file.type, videoView],
  );

  const handleDrag = useCallback(
    (event: DragEvent) => {
      if (!dragInFlightRef.current || !onMediaToCanvasDragModifier) return;
      onMediaToCanvasDragModifier(event.altKey);
    },
    [onMediaToCanvasDragModifier],
  );

  const handleDragEnd = useCallback(() => {
    if (!dragInFlightRef.current) return;
    dragInFlightRef.current = false;
    onMediaToCanvasDragEnd?.();
  }, [onMediaToCanvasDragEnd]);

  return (
    <Box
      data-testid={mediaToCanvasDragActive ? `media-grid-tile-${nodeId}-${index}` : undefined}
      data-e2e-video-paused={file.type === 'video' ? String(videoState?.paused ?? true) : undefined}
      draggable={mediaToCanvasDragActive}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      onPointerDown={handlePointerDown}
      onClick={handleFileClick}
      sx={{
        borderRadius: 2.5,
        transition: 'opacity 0.12s ease-in-out, all 0.1s ease-in-out',
        ...(mediaToCanvasDragSourceDimmed && { opacity: 0.16 }),
        overflow: 'hidden',
        height: fixedItemSize ? `${fixedItemSize}px` : 'auto',
        ...(fillHeight && { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }),
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 2.5,
          pointerEvents: 'none',
          zIndex: 2,
          border: isSelected ? `2px solid ${themeColor.white40}` : `1px solid ${color.White04_T}`,
        },

        '&::before': {
          // this is used to create a hover effect
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isSelected ? 'transparent' : 'rgba(14, 14, 19, 0.5)',
          transition: 'background-color 0.1s ease-in-out',
          pointerEvents: 'none',
          zIndex: 1,
        },
        '&:hover::before': {
          backgroundColor: isSelected ? 'transparent' : 'rgba(14, 14, 19, 0.3)',
        },
      }}
      className="nodrag"
    >
      <FileViewer
        isMultiFilesViewMode={!fillHeight}
        isSelected={isSelected}
        id={nodeId}
        index={index}
        asset={file}
        isClickable={true}
        fixedItemSize={fixedItemSize}
        handleContextMenu={handleContextMenuForItem}
        onTextChange={onTextChange}
        suspendMediaPlayback={suspendMediaPlayback}
      />
    </Box>
  );
};
